import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { BehaviorSubject, Observable, catchError, take, tap, throwError } from 'rxjs';
import { RESPONCE } from '../../model/responce';
import { PHASE_RESPONSE } from '../../model/signUp.model';
import {
  REGISTRATION_PHASES_INPUT,
  REGISTRATION_PHASES_RESPONSE,
  GET_SERVICES_INPUT,
  SERVICE,
  BUSINESS_SERVICE_INPUT,
  POSITION,
  BUSINESS_EMPLOYEE_INPUT,
  WORKING_DAY,
  WORKING_HOUR_INPUT,
  SKIP_INPUT,
  SKIP_RESPONSE,
  REGITRATIONCYCLEPHASTHREEMODELSTRING,
} from '../../model/step-phases.model';
import { ApiService } from '../api.service';
import { SharedService } from '../shared.service';

@Injectable({
  providedIn: 'root',
})
export class BusinessDetailsService {
  /** Latest full registration phases payload from the API (starts as null until loaded). */
  public registeredPhasesSubject: BehaviorSubject<REGISTRATION_PHASES_RESPONSE> =
    new BehaviorSubject<REGISTRATION_PHASES_RESPONSE>(null);

  /** Current active step index in the registration stepper UI (1-based). */
  steperActive = new BehaviorSubject<number>(1);

  constructor(
    private readonly apiService: ApiService,
    private readonly router: Router,
    private readonly sharedService: SharedService,
    private readonly toastr: ToastrService,
    private readonly translateService: TranslateService
  ) {}

  /** Saves phase-three business details (multipart FormData) to the backend. */
  savePhaseTwo(data: FormData): Observable<RESPONCE<PHASE_RESPONSE>> {
    return this.apiService.post<FormData, RESPONCE<PHASE_RESPONSE>>(
      'RegistrationCyclePhaseThree/SavePhaseThreeData',
      data
    );
  }

  /** Saves tax setup (phase seven) string payload returned from the tax UI flow. */
  saveTaxSetup(payload: REGITRATIONCYCLEPHASTHREEMODELSTRING): Observable<RESPONCE<PHASE_RESPONSE>> {
    return this.apiService.post<REGITRATIONCYCLEPHASTHREEMODELSTRING, RESPONCE<PHASE_RESPONSE>>(
      'RegistrationCyclePhaseSeven/SavePhaseSevenData',
      payload
    );
  }

  /**
   * Fetches saved registration phase data; on success (state 100) mirrors Result into SharedService via setRegisteredPhasesResult.
   */
  getRegisteredPhases(
    data: REGISTRATION_PHASES_INPUT
  ): Observable<RESPONCE<REGISTRATION_PHASES_RESPONSE>> {
    return this.apiService.get<RESPONCE<REGISTRATION_PHASES_RESPONSE>>(
      'RegistrationCycleGeneral/GetRegisteredPhasesData',
      data
    ).pipe(
      take(1),
      tap(res => {
        const result = res?.Result;
        if (!result) return;
        if (res.State === 100) {
          this.sharedService.setRegisteredPhasesResult(result as Parameters<SharedService['setRegisteredPhasesResult']>[0]);
        }
      }),
      catchError((error) => {
        this.toastr.error(
          this.translateService.instant('Registration_Failed_To_Load_User_Data')
        );

        this.router.navigate(['registration-cycle']);
        return throwError(() => error);
      })
    );
  }

  /** Lists services available to attach to the business for phase four/six flows. */
  getServices(data: GET_SERVICES_INPUT): Observable<RESPONCE<SERVICE[]>> {
    return this.apiService.get<RESPONCE<SERVICE[]>>(
      'RegistrationCyclePhaseFour/GetServices',
      data
    );
  }

  /** Saves selected business services (phase six). */
  saveBusinessService(
    data: BUSINESS_SERVICE_INPUT
  ): Observable<RESPONCE<PHASE_RESPONSE>> {
    return this.apiService.post<
      BUSINESS_SERVICE_INPUT,
      RESPONCE<PHASE_RESPONSE>
    >('RegistrationCyclePhaseSix/SavePhaseSixData', data);
  }

  /** Loads job positions for employee/branch forms (phase five). */
  getPositions(): Observable<RESPONCE<POSITION[]>> {
    return this.apiService.get<RESPONCE<POSITION[]>>(
      'RegistrationCyclePhaseFive/GetPositions'
    );
  }

  /** Saves employees and branch data (phase five). */
  saveEmployeeAndBranch(
    data: BUSINESS_EMPLOYEE_INPUT
  ): Observable<RESPONCE<PHASE_RESPONSE>> {
    return this.apiService.post<
      BUSINESS_EMPLOYEE_INPUT,
      RESPONCE<PHASE_RESPONSE>
    >('RegistrationCyclePhaseFive/SavePhaseFiveData', data);
  }

  /** Loads week-day definitions for working-hours configuration (phase four/six). */
  getWeekDays(): Observable<RESPONCE<WORKING_DAY[]>> {
    return this.apiService.get<RESPONCE<WORKING_DAY[]>>(
      'RegistrationCyclePhaseSix/GetWeekDays'
    );
  }

  /** Persists working hours for the registration cycle (phase four). */
  saveWorkingHours(data: WORKING_HOUR_INPUT): Observable<RESPONCE<PHASE_RESPONSE>> {
    return this.apiService.post<WORKING_HOUR_INPUT, RESPONCE<PHASE_RESPONSE>>(
      'RegistrationCyclePhaseFour/SavePhaseFourData',
      data
    );
  }

  /** Returns true if any working day row is missing StartTime or FinishTime (invalid before save). */
  checkTimeValidation(items: WORKING_DAY[]): boolean {
    let checked: boolean = false;
    for (let item of items) {
      if (!item.StartTime || !item.FinishTime) {
        checked = true;
      }
    }
    return checked;
  }

  /** Skips the current registration phase per backend rules and returns the updated skip state. */
  skip(data: SKIP_INPUT): Observable<RESPONCE<SKIP_RESPONSE>> {
    return this.apiService.post<SKIP_INPUT, RESPONCE<SKIP_RESPONSE>>(
      'RegistrationCycleGeneral/Skip',
      data
    );
  }
}
